import { z } from 'zod';

// Define the schema for a line item in the sales invoice
export const salesInvoiceItemSchema = z.object({
  id: z.number().optional(), // Only present when editing an existing item
  item_id: z.number({ required_error: 'Product is required' }),
  quantity: z.number().min(0.01, { message: 'Quantity must be greater than 0' }),
  rate: z.number().min(0, { message: 'Rate must be 0 or greater' }),
  discount_percent: z.number().min(0).max(100).default(0),
  discount_amount: z.number().min(0).default(0), // Add discount_amount field
  tax_rate_id: z.number().nullable().optional(), // Add tax_rate_id field
  amount: z.number(),
  description: z.string().optional().nullable(),
  godown_id: z.number({ required_error: 'Warehouse is required' }),
  applied_sale_rule_id: z.number().nullable().optional(), // Add applied_sale_rule_id field
  // Additional fields specific to invoice items if needed
}).refine((data) => {
  // Ensure only one type of discount is provided
  if (data.discount_percent > 0 && data.discount_amount > 0) {
    return false;
  }
  return true;
}, {
  message: "Provide either discount percentage or discount amount, not both",
  path: ["discount_percent"], // Show error on discount_percent field
});

// Define the schema for the entire sales invoice form
export const salesInvoiceSchema = z.object({
  voucher_type_id: z.number({ required_error: 'Voucher type is required' }),
  party_id: z.number({ required_error: 'Customer is required' }),
  site_id: z.number({ required_error: 'Site is required' }),
  transaction_date: z.date({ required_error: 'Date is required' }),
  due_date: z.date().optional().nullable(),
  reference_number: z.string().optional().nullable(),
  narration: z.string().optional().nullable(),
  total_amount: z.number().min(0),
  discount_type: z.enum(['percentage', 'fixed']).optional().nullable(),
  discount_value: z.number().min(0).optional().nullable(),
  delivery_charge: z.number().min(0).optional().nullable(), // New field for delivery charges
  items: z.array(salesInvoiceItemSchema)
    .min(1, { message: 'At least one product is required' }),
}).refine((data) => {
  // Validate discount percentage doesn't exceed 100%
  if (data.discount_type === 'percentage' && data.discount_value && data.discount_value > 100) {
    return false;
  }
  // Validate fixed discount doesn't exceed total amount
  if (data.discount_type === 'fixed' && data.discount_value && data.discount_value > data.total_amount) {
    return false;
  }
  return true;
}, {
  message: "Discount percentage cannot exceed 100% or fixed discount cannot exceed total amount",
  path: ["discount_value"],
});

// Create a type for form values based on the schema
export type SalesInvoiceFormValues = z.infer<typeof salesInvoiceSchema>;

// Create a type for line items
export type SalesInvoiceItemFormValues = z.infer<typeof salesInvoiceItemSchema>; 